import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Card, CardBody } from "@progress/kendo-react-layout";
import { useInjection } from "@presentation/components/dependencyInjection";
import { TYPES } from "@utils/inversifyJS/types";
import type { ListProjectsService } from "@application/useCases/workspace/ListProjectsService";
import type { ListTeamsService } from "@application/useCases/workspace/ListTeamsService";
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { useWorkspaceContextStore } from "@presentation/stores/WorkspaceContextStore";
import { mutedTextStyle, pageFillContentStyle, pageHeaderRegionStyle, pageHeaderStyle, pageShellStyle, pageTitleStyle, sectionTitleStyle } from "@presentation/common/pageLayout";

/**
Shown in place of a page when the workspace has loaded and the caller holds neither a team nor a
project role - tells them who can grant access and lets them re-check once they have been added,
without having to sign out and back in.
*/
export const NoWorkspaceAccess: React.FC = () => {
  const listTeamsService = useInjection<ListTeamsService>(TYPES.ListTeamsService);
  const listProjectsService = useInjection<ListProjectsService>(TYPES.ListProjectsService);
  const username = useUserManagementStore((state) => state.userSession?.username);
  const setWorkspace = useWorkspaceContextStore((state) => state.setWorkspace);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [refreshError, setRefreshError] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    setRefreshError(false);
    try {
      const [teams, projects] = await Promise.all([listTeamsService.getMyTeamsAsync(), listProjectsService.getMyProjectsAsync()]);
      setWorkspace(teams, projects);
    } catch {
      setRefreshError(true);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCopy = async () => {
    if (!username) return;
    try {
      await navigator.clipboard.writeText(username);
      setIsCopied(true);
    } catch {
      setIsCopied(false);
    }
  };

  return (
    <div style={pageShellStyle}>
      <div style={pageHeaderRegionStyle}>
        <div style={pageHeaderStyle}>
          <h1 style={pageTitleStyle}>No access yet</h1>
        </div>
      </div>
      <div style={pageFillContentStyle}>
        <Card style={{ maxWidth: 640 }}>
          <CardBody style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div role="status" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <h2 style={sectionTitleStyle}>You are not part of any team or project yet</h2>
              <p style={mutedTextStyle}>
                To start using OmicsFlow, ask a <strong>team manager</strong> to add you to a team, or a <strong>project manager</strong> to give you a role on a project. Your deployment{" "}
                <strong>administrator</strong> can also grant you access.
              </p>
              {username && (
                <p style={mutedTextStyle}>
                  Share your username with them so they can find you: <strong>{username}</strong>{" "}
                  <Button type="button" fillMode="flat" size="small" onClick={() => void handleCopy()}>
                    {isCopied ? "Copied" : "Copy"}
                  </Button>
                </p>
              )}
              <p style={mutedTextStyle}>Once you have been added, check again to load your workspace.</p>
              {refreshError && (
                <p role="alert" style={{ ...mutedTextStyle, color: "#b91c1c" }}>
                  Could not check your access. Try again.
                </p>
              )}
              <div>
                <Button type="button" themeColor="primary" disabled={isRefreshing} onClick={() => void handleRefresh()}>
                  {isRefreshing ? "Checking..." : "Check again"}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};
